import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Checkboxes from 'components/Form/Checkboxes';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import {setMessage} from 'hooks/useMessage';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {DocumentTypeEntry} from '../types';
import {TRNDEditFormFields, usePostTRNDEdit} from './services';

export type TRNDBackendData = Backend<
    {
        documentTypes: Array<DocumentTypeEntry>;
        mid: string;
    },
    'canEdit'
>;

export type TRNDFormFields = FormFields<TRNDEditFormFields>;

const TaxRegistrationNumberDisplay = () => {
    const {
        appPermissions,
        data: {documentTypes, mid},
    } = useBackend<TRNDBackendData>();

    const getInitialFormValues = () =>
        Object.fromEntries(
            documentTypes.map((documentType) => [
                documentType.documentType,
                documentType.enabled || false,
            ]),
        );

    const methods = useForm<TRNDFormFields>({
        defaultValues: getInitialFormValues(),
    });

    const {mutateAsync} = usePostTRNDEdit();

    const {handleSubmit} = methods;

    const {t} = useTranslation('Document.TaxRegistrationNumberDisplay');

    const onSubmit = (formData: TRNDFormFields) => {
        if (!appPermissions.canEdit.allowed) {
            return;
        }

        mutateAsync({formData, mid})
            .then(() => {
                setUrl(window.Urls.react_tax_registration_number_view());
                setMessage('successMessage', t('successMessage'));
            })
            .catch(onApiFormError(methods));
    };

    return (
        <div>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <NavigationTabs.DocumentSettings selection="taxRegistrationNumber" />
                    <FormHeader
                        button={{
                            disabled: !appPermissions.canEdit.allowed,
                            kind: 'primary',
                            label: t('form:save'),
                            type: 'submit',
                        }}
                        subtitle={t('subTitle')}
                        title={t('title')}
                        tooltipMessage={appPermissions.canEdit.failureReason}
                    />
                    <Checkboxes
                        classNameGroup="my-4"
                        options={documentTypes.map((documentType) => ({
                            label: documentType.label,
                            name: documentType.documentType,
                        }))}
                    />
                    <FormResult className="mt-2" />
                </form>
            </FormProvider>
        </div>
    );
};

export default TaxRegistrationNumberDisplay;
